/**
 * Commercial subsite event tracking.
 *
 * Every event goes three places, and each is independent:
 *
 *   dataLayer  always, so a GTM container can pick them up if one is ever added
 *   gtag       direct to GA4, so events land WITHOUT needing GTM at all
 *   clarity    as a custom event plus a tag, so sessions can be filtered by them
 *
 * GA4 and Clarity are already live on this storefront (G-5DD746PJNY and Clarity
 * project vkask1aj2i), so the direct calls work today with no extra setup.
 *
 * Labels: any element can carry data-track-label to name itself. Where a
 * section exposes a label setting, a marketer can rename it in the customizer
 * without a developer. Sections also set data-track-section, which is inherited
 * by every button inside them, so an unlabelled button still reports where it
 * was.
 */
(() => {
  const dl = () => (window.dataLayer = window.dataLayer || []);

  /**
   * GA4 rejects anything that is not a plain scalar, and silently drops the
   * whole event rather than the offending parameter, so values are coerced.
   */
  const clean = (payload) => {
    const out = {};
    Object.keys(payload || {}).forEach((k) => {
      const v = payload[k];
      if (v === undefined || v === null || v === '') return;
      out[k] = typeof v === 'boolean' || typeof v === 'number' ? v : String(v).slice(0, 100);
    });
    return out;
  };

  const push = (event, payload) => {
    const params = clean(payload);

    dl().push(Object.assign({ event }, params));

    // Direct to GA4. gtag is already on the page via the Google & YouTube app,
    // so no GTM container is required for these to be collected.
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, params);
    }

    // Clarity: an event to filter recordings by, plus the most useful
    // parameter as a tag so sessions can be segmented on it.
    if (typeof window.clarity === 'function') {
      try {
        window.clarity('event', event);
        const tag = params.click_label || params.option_field || params.form_label;
        if (tag) window.clarity('set', event, String(tag));
      } catch (e) {
        // Clarity throws if called before it finishes booting; losing a tag is
        // not worth breaking the click handler for.
      }
    }
  };

  /** Page identity, attached to every event so the same form on two pages is distinguishable. */
  const pageContext = () => ({
    page_path: window.location.pathname,
    page_title: document.title,
  });

  const labelFor = (el) => {
    // Only an explicit label on the element or a wrapper *inside* the section
    // counts. The section root names the section, not every button in it.
    const own = el.closest('[data-track-label]');
    if (own && !own.hasAttribute('data-track-section')) return own.dataset.trackLabel;
    // Fall back to trimmed visible text so an unlabelled button is still legible.
    const text = (el.textContent || '').trim().replace(/\s+/g, ' ');
    return text.slice(0, 60) || 'unlabelled';
  };

  const sectionFor = (el) => {
    const s = el.closest('[data-track-section]');
    return s ? s.dataset.trackSection : 'unknown';
  };

  /* ---------------------------------------------------------------- clicks */

  document.addEventListener('click', (event) => {
    const el = event.target.closest('a, button');
    if (!el || !el.closest('.commercial, body.commercial')) return;

    const section = sectionFor(el);
    const label = labelFor(el);

    push('commercial_click', Object.assign(pageContext(), {
      click_section: section,
      click_label: label,
      click_text: (el.textContent || '').trim().slice(0, 80),
      click_url: el.getAttribute('href') || '',
      click_tier: (el.className.match(/c-cta--(\d)/) || [])[1] || '',
    }));

    // The hero CTA is called out separately because it is reported on by itself.
    if (section === 'herobanner') {
      push('commercial_hero_cta', Object.assign(pageContext(), { click_label: label }));
    }
  }, true);

  /* ------------------------------------------------------- showroom booked */

  // The booking calendars run in cross-origin iframes, so a finished booking is
  // only visible through the message each one posts to the page. Calendly is
  // the live one; HubSpot Meetings is the showroom section's fallback.
  const HUBSPOT_MEETINGS = /^https:\/\/meetings(-[a-z0-9]+)?\.hubspot\.com$/;

  window.addEventListener('message', (event) => {
    const data = event.data || {};
    let tool = '';
    if (event.origin === 'https://calendly.com' && data.event === 'calendly.event_scheduled') tool = 'calendly';
    if (HUBSPOT_MEETINGS.test(event.origin) && data.meetingBookSucceeded) tool = 'hubspot-meetings';
    if (!tool) return;
    push('commercial_showroom_booked', Object.assign(pageContext(), { booking_tool: tool }));
  });

  /* ------------------------------------------------------- HubSpot forms */

  // Forms from HubSpot's forms editor (the trade-in form) announce a finished
  // submission on window. The section that holds the form names it, so GTM can
  // tell the trade-in form apart from any other HubSpot form on the site.
  window.addEventListener('hs-form-event:on-submission:success', (event) => {
    const formId = (event.detail && event.detail.formId) || '';
    const holder = formId && document.querySelector(`[data-form-id="${CSS.escape(formId)}"][data-track-form]`);
    push('commercial_hubspot_form_submit', Object.assign(pageContext(), {
      form_id: formId,
      form_label: holder ? holder.dataset.trackForm : 'hubspot-form',
    }));
  });

  /* ------------------------------------------------------- quote funnel */

  const qb = document.querySelector('commercial-quote-builder');
  if (!qb) return;

  const formLabel = qb.dataset.formLabel || 'quote-builder';

  const funnel = (event, extra) =>
    push(event, Object.assign(pageContext(), { form_label: formLabel }, extra || {}));

  // (2) an option was selected
  qb.addEventListener('click', (event) => {
    const chip = event.target.closest('.c-chip');
    if (!chip) return;
    funnel('commercial_form_option', {
      option_field: chip.dataset.label || chip.dataset.field || '',
      option_value: chip.dataset.labelValue || chip.dataset.value || '',
    });
  });

  qb.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.dataset || !input.dataset.field || input.type !== 'checkbox') return;
    funnel('commercial_form_option', {
      option_field: input.dataset.label || input.dataset.field,
      option_value: input.value,
      option_checked: input.checked,
    });
  });

  // (1) every required field in the current step is answered
  const stepComplete = new Set();
  const checkStep = () => {
    const step = qb.querySelector('[data-step]:not([hidden])');
    if (!step) return;
    const id = step.dataset.step;

    const chipFields = new Set(
      Array.from(step.querySelectorAll('.c-chip[data-required]')).map((c) => c.dataset.field)
    );
    const chipsDone = Array.from(chipFields).every((f) =>
      step.querySelector(`.c-chip[data-field="${CSS.escape(f)}"][aria-pressed="true"]`)
    );
    const inputsDone = Array.from(
      step.querySelectorAll('input[data-required], textarea[data-required]')
    ).every((i) => i.value.trim());

    if (chipsDone && inputsDone && !stepComplete.has(id)) {
      stepComplete.add(id);
      funnel('commercial_form_step_complete', { form_step: Number(id) });
    }
  };

  qb.addEventListener('click', checkStep);
  qb.addEventListener('input', checkStep);
  qb.addEventListener('change', checkStep);

  // (3) final submission, fired on success rather than on click
  qb.addEventListener('commercial:quote-submitted', (event) => {
    funnel('commercial_form_submit', {
      form_destination: (event.detail && event.detail.destination) || 'unknown',
    });
  });
})();
